import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { useSportsLang } from '../contexts/SportsLangContext';
import { VUKZ } from '../theme/vukzTheme';
import { useRBAC } from '../contexts/RBACContext';
import { useActiveContext } from '../contexts/ActiveContextContext';
import { R } from '../navigation/routeRegistry';
import {
  canViewAthleteDetail,
  canEditTreinoFisico,
  viewAsForTrainingRequests,
} from '../lib/rbacPermissions';
import {
  getTrainingStats,
  listPlans,
  getPlanDetail,
  completePlan,
  getExecutionsSummary,
  createExecution,
  getExecution,
  updateExecution,
  updateExecutionItem,
  completeExecution,
} from '../services/trainingV2Service';
import {
  ClipboardList,
  Dumbbell,
  CheckCircle2,
  Clock,
  ChevronRight,
  ChevronDown,
  X,
  Star,
  AlertTriangle,
  Users,
  Activity,
  Calendar,
  Play,
  Eye,
} from 'lucide-react';

/**
 * TreinoFisicoPage — Treino Físico (Physical Training)
 *
 * Role-aware:
 *   - ATLETA: Sees assigned plans, executes them, gives feedback
 *   - TREINADOR: Views all training plans + execution metrics (read-only)
 *   - PREPARADOR_FISICO: Redirected to /prep/training (existing full page)
 *
 * Uses /api/vukz/training/* consolidated API (trainingV2Service).
 */

// ── Status styles (lang-independent) ──
const STATUS_STYLES = {
  draft:       { bg: '#f3f4f6', color: '#6b7280', icon: Clock },
  planned:     { bg: '#fef3c7', color: '#92400e', icon: Calendar },
  published:   { bg: '#d1fae5', color: '#065f46', icon: ClipboardList },
  in_progress: { bg: '#dbeafe', color: '#1e40af', icon: Play },
  completed:   { bg: '#f0fdf4', color: '#166534', icon: CheckCircle2 },
  skipped:     { bg: '#f3f4f6', color: '#6b7280', icon: X },
  cancelled:   { bg: '#fef2f2', color: '#991b1b', icon: X },
};

const getStatusLabels = (lang) => {
  const e = lang === 'en';
  return {
    draft: e ? 'Draft' : 'Rascunho',
    planned: e ? 'Scheduled' : 'Agendado',
    published: e ? 'Published' : 'Publicado',
    in_progress: e ? 'In Progress' : 'Em Andamento',
    completed: e ? 'Completed' : 'Concluído',
    skipped: e ? 'Skipped' : 'Pulado',
    cancelled: e ? 'Cancelled' : 'Cancelado',
  };
};

const getT = (lang) => {
  const e = lang === 'en';
  return {
    titleAthlete: e ? 'My Physical Training' : 'Meu Treino Físico',
    titleCoach: e ? 'Physical Training' : 'Treino Físico',
    subtitleAthlete: e ? 'Your training plans and tracking' : 'Seus planos de treino e acompanhamento',
    subtitleCoach: e ? 'View training and athlete tracking' : 'Visualize os treinos e acompanhamento dos atletas',
    tabMyTrainings: e ? 'My Workouts' : 'Meus Treinos',
    tabTrainings: e ? 'Workouts' : 'Treinos',
    tabExecutions: e ? 'Executions' : 'Execuções',
    statToday: e ? 'Workouts Today' : 'Treinos Hoje',
    statPending: e ? 'Pending' : 'Pendentes',
    statFeedbackPending: e ? 'Feedback Pending' : 'Feedback Pendente',
    statRpe: e ? 'Avg RPE 7d' : 'RPE Médio 7d',
    statCompleted: e ? 'Completed 7d' : 'Concluídos 7d',
    noPlansAthlete: e ? 'No workouts assigned' : 'Nenhum treino atribuído',
    noPlansCoach: e ? 'No workouts found' : 'Nenhum treino encontrado',
    noPlansAthleteHint: e ? 'Your physical trainer has not yet assigned workouts for you.' : 'Seu preparador físico ainda não atribuiu treinos para você.',
    noPlansCoachHint: e ? 'Workouts created by the physical trainer will appear here.' : 'Os treinos criados pelo preparador físico aparecerão aqui.',
    groupActive: e ? 'Active Workouts' : 'Treinos Ativos',
    groupDrafts: e ? 'Drafts' : 'Rascunhos',
    groupPast: e ? 'Previous' : 'Anteriores',
    sportGeneral: e ? 'General' : 'Geral',
    dateLocale: e ? 'en-US' : 'pt-BR',
    back: e ? '← Back' : '← Voltar',
    executionInProgress: e ? 'Execution in progress' : 'Execução em andamento',
    concludeWorkout: e ? '✓ Finish Workout' : '✓ Concluir Treino',
    startWorkout: e ? 'Start Workout' : 'Iniciar Treino',
    loadingExec: e ? 'Loading...' : 'Carregando...',
    noExercise: e ? 'No exercises' : 'Nenhum exercício',
    exerciseFallback: e ? 'Exercise' : 'Exercício',
    blockLabel: (n) => e ? `Block ${n}` : `Bloco ${n}`,
    restLabel: (s) => e ? `${s}s rest` : `${s}s desc.`,
    feedbackTitle: e ? 'Athlete Feedback' : 'Feedback do Atleta',
    fbRpe: 'RPE',
    fbFeeling: e ? 'Feeling' : 'Sensação',
    fbCompletion: e ? 'Completion' : 'Conclusão',
    noExecutions: e ? 'No executions in the last 7 days' : 'Nenhuma execução nos últimos 7 dias',
    noExecutionsHint: e ? 'Athletes have not yet completed workouts in this period.' : 'Os atletas ainda não completaram treinos no período.',
    colAthlete: e ? 'Athlete' : 'Atleta',
    colTotal: 'Total',
    colCompleted: e ? 'Completed' : 'Completados',
    colAvgRpe: e ? 'Avg RPE' : 'RPE Médio',
    colCompletion: e ? '% Completion' : '% Conclusão',
    modalTitle: e ? 'Workout Feedback' : 'Feedback do Treino',
    rpeLabel: e ? 'Perceived Exertion (RPE):' : 'Esforço Percebido (RPE):',
    rpeNone: e ? 'None' : 'Nenhum',
    rpeMax: e ? 'Maximum' : 'Máximo',
    feelingLabel: e ? 'How did you feel?' : 'Como se sentiu?',
    feelingGreat: e ? '💪 Great' : '💪 Ótimo',
    feelingGood: e ? '👍 Good' : '👍 Bom',
    feelingNormal: '😐 Normal',
    feelingTired: e ? '😓 Tired' : '😓 Cansado',
    feelingPain: e ? '🤕 Pain' : '🤕 Dor',
    notesLabel: e ? 'Notes (optional)' : 'Observações (opcional)',
    notesPlaceholder: e ? 'How was the workout? Any discomfort?' : 'Como foi o treino? Algum desconforto?',
    submitting: e ? 'Submitting...' : 'Enviando...',
    confirm: e ? 'Confirm Completion' : 'Confirmar Conclusão',
  };
};

const StatusBadge = ({ status }) => {
  const { lang } = useSportsLang();
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const label = getStatusLabels(lang)[status] || status;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      <s.icon size={12} />
      {label}
    </span>
  );
};

// ══════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════
const TreinoFisicoPage = () => {
  const { activeRole, hasAnyRole, userId } = useRBAC();
  const { clubId } = useActiveContext();
  const { lang } = useSportsLang();
  const t = getT(lang);

  // ── Permissões: SEPARAR visibilidade de edição ──
  //
  // Antes (fix B5 anterior): `isCoach = TREINADOR || PREPARADOR || GESTOR`
  // misturava "ver" com "editar" — Gestor e Treinador ganhavam
  // tratamento de "coach" inclusive em rascunhos e tabs de edição, e
  // o backend (que aceita `viewAs=coach` só como filtro de visão) era
  // o único guard. Agora separamos:
  //
  //   • viewAsCoach    → quem VÊ a lista ampla (todos os atletas):
  //                      Treinador, Preparador, Gestor (e demais staff).
  //   • isAthleteView  → tudo que não for staff (incl. role=null durante
  //                      load → default seguro para não cair em 403).
  //   • canEditFisico  → SÓ Preparador (regra: treino físico é dele).
  //                      Gestor/Treinador entram em modo read-only.
  //
  // Treino físico nesta tela: VER é amplo, EDITAR é estreito.
  const viewAsCoach = canViewAthleteDetail(activeRole);
  const isAthleteView = !viewAsCoach;
  const canEditFisico = canEditTreinoFisico(activeRole);

  // Aliases retrocompatíveis para não reescrever toda a árvore de
  // componentes filhos que já recebem `isAthlete`/`isCoach` por prop.
  // SEMÂNTICA agora é estritamente "modo de visualização", não "pode
  // editar". Para checar edição, use `canEditFisico`.
  const isAthlete = isAthleteView;

  // ── State ──
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Execution canvas state
  const [executionData, setExecutionData] = useState(null);
  const [loadingExecution, setLoadingExecution] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // ── Fetch plans ──
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      // viewAs derivado do helper canônico — staff (incl. Gestor) vê
      // tudo; atleta e role-em-loading caem em 'athlete' (default seguro).
      const viewAs = viewAsForTrainingRequests(activeRole);
      // planType='physical': esta página é TREINO FÍSICO — sem o filtro ela
      // listava os 6.241 planos mixed legados (natação), que apareciam aqui
      // disfarçados de físico. Backend aplica em WHERE (routes_training_
      // consolidated). Base sem plano physical real → empty state honesto
      // ("seu preparador ainda não atribuiu treinos") até o seed criar um.
      const result = await listPlans({ viewAs, clubId, planType: 'physical' });
      setPlans(result.data || []);
    } catch (err) {
      console.error('[TreinoFisico] Plans error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeRole, clubId]);

  // ── Fetch stats ──
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const viewAs = viewAsForTrainingRequests(activeRole);
      const result = await getTrainingStats(viewAs, clubId);
      setStats(result.data?.summary || null);
    } catch (err) {
      console.error('[TreinoFisico] Stats error:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [activeRole, clubId]);

  // ── Fetch executions (coach) ──
  const fetchExecutions = useCallback(async () => {
    if (isAthlete) return;
    try {
      const result = await getExecutionsSummary({ clubId, days: 7 });
      setExecutions(result.data?.athletes || []);
    } catch (err) {
      console.error('[TreinoFisico] Executions error:', err);
    }
  }, [isAthlete, clubId]);

  useEffect(() => {
    fetchPlans();
    fetchStats();
  }, [fetchPlans, fetchStats]);

  useEffect(() => {
    if (activeTab === 'executions') fetchExecutions();
  }, [activeTab, fetchExecutions]);

  // ── Plan detail ──
  const openPlanDetail = async (planId) => {
    setLoadingDetail(true);
    setSelectedPlan(null);
    setExecutionData(null);
    try {
      const result = await getPlanDetail(planId);
      setSelectedPlan(result.data || null);
    } catch (err) {
      console.error('[TreinoFisico] Detail error:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Start / Resume execution (athlete) ──
  const startExecution = async (planId) => {
    setLoadingExecution(true);
    try {
      const result = await createExecution(planId, userId);
      if (result.data?.execution_id) {
        const exec = await getExecution(result.data.execution_id);
        setExecutionData(exec.data || null);
      }
    } catch (err) {
      console.error('[TreinoFisico] Execution error:', err);
    } finally {
      setLoadingExecution(false);
    }
  };

  // ── Toggle item done (athlete) ──
  const toggleItemDone = async (itemId, currentDone) => {
    if (!executionData) return;
    try {
      await updateExecutionItem(executionData.execution_id, itemId, { is_done: !currentDone });
      const exec = await getExecution(executionData.execution_id);
      setExecutionData(exec.data || null);
    } catch (err) {
      console.error('[TreinoFisico] Toggle item error:', err);
    }
  };

  // ── Complete execution (athlete) ──
  const handleComplete = async (feedback) => {
    if (!executionData) return;
    try {
      await completeExecution(executionData.execution_id, feedback);
      setExecutionData(null);
      setSelectedPlan(null);
      setFeedbackOpen(false);
      fetchPlans();
      fetchStats();
    } catch (err) {
      console.error('[TreinoFisico] Complete error:', err);
    }
  };

  // ── Tabs config ──
  const tabs = isAthlete
    ? [{ key: 'plans', label: t.tabMyTrainings, icon: Dumbbell }]
    : [
        { key: 'plans', label: t.tabTrainings, icon: ClipboardList },
        { key: 'executions', label: t.tabExecutions, icon: Activity },
      ];

  return (
    <PageLayout
      header={{
        title: isAthlete ? t.titleAthlete : t.titleCoach,
        subtitle: isAthlete ? t.subtitleAthlete : t.subtitleCoach,
      }}
    >
      {/* ── KPI Cards ── */}
      <StatsCards stats={stats} loading={loadingStats} isAthlete={isAthlete} />

      {/* ── Tabs ── */}
      <div className="rounded-xl border mb-6" style={{ borderColor: VUKZ.border }}>
        <div className="border-b px-6 flex gap-6" style={{ borderColor: VUKZ.border }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className="py-3 text-sm font-medium border-b-2 transition-all flex items-center gap-1.5"
              style={{
                borderColor: activeTab === t.key ? VUKZ.primary : 'transparent',
                color: activeTab === t.key ? VUKZ.text : VUKZ.textLight,
              }}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'plans' && (
            selectedPlan ? (
              <PlanDetailView
                plan={selectedPlan}
                loading={loadingDetail}
                isAthlete={isAthlete}
                executionData={executionData}
                loadingExecution={loadingExecution}
                onBack={() => { setSelectedPlan(null); setExecutionData(null); }}
                onStartExecution={startExecution}
                onToggleItem={toggleItemDone}
                onOpenFeedback={() => setFeedbackOpen(true)}
              />
            ) : (
              <PlansList
                plans={plans}
                loading={loading}
                isAthlete={isAthlete}
                canEditFisico={canEditFisico}
                onSelect={openPlanDetail}
              />
            )
          )}

          {activeTab === 'executions' && !isAthlete && (
            <ExecutionsOverview executions={executions} />
          )}
        </div>
      </div>

      {/* ── Feedback Modal ── */}
      {feedbackOpen && (
        <FeedbackModal
          onSubmit={handleComplete}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
// Stats Cards
// ══════════════════════════════════════════════════════════════
const StatsCards = ({ stats, loading, isAthlete }) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }
  if (!stats) return null;

  const cards = isAthlete
    ? [
        { label: t.statToday, value: stats.plans_today || 0, icon: Dumbbell },
        { label: t.statPending, value: stats.pending_feedback || 0, icon: Clock },
        { label: t.statRpe, value: stats.avg_rpe_7d != null ? stats.avg_rpe_7d : '—', icon: Activity },
        { label: t.statCompleted, value: `${stats.completed_7d || 0}/${stats.total_7d || 0}`, icon: CheckCircle2 },
      ]
    : [
        { label: t.statToday, value: stats.plans_today || 0, icon: Dumbbell },
        { label: t.statFeedbackPending, value: stats.pending_feedback || 0, icon: Clock },
        { label: t.statRpe, value: stats.avg_rpe_7d != null ? stats.avg_rpe_7d : '—', icon: Activity },
        { label: t.statCompleted, value: `${stats.completed_7d || 0}/${stats.total_7d || 0}`, icon: CheckCircle2 },
      ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((c, i) => (
        <div
          key={i}
          className="rounded-xl border p-4 flex items-center gap-3"
          style={{ borderColor: VUKZ.border }}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: VUKZ.primaryLight }}
          >
            <c.icon size={18} style={{ color: VUKZ.primary }} />
          </div>
          <div>
            <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>{c.label}</p>
            <p className="text-lg font-bold" style={{ color: VUKZ.text }}>{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Plans List
// ══════════════════════════════════════════════════════════════
const PlansList = ({ plans, loading, isAthlete, canEditFisico = false, onSelect }) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="text-center py-16">
        <Dumbbell size={48} className="mx-auto mb-4" style={{ color: VUKZ.textLight }} />
        <h3 className="text-base font-bold mb-2" style={{ color: VUKZ.text }}>
          {isAthlete ? t.noPlansAthlete : t.noPlansCoach}
        </h3>
        <p className="text-sm" style={{ color: VUKZ.textLight }}>
          {isAthlete ? t.noPlansAthleteHint : t.noPlansCoachHint}
        </p>
      </div>
    );
  }

  // Separate into active and past
  const active = plans.filter(p => ['published', 'planned', 'in_progress'].includes(p.status));
  const past = plans.filter(p => ['completed', 'skipped', 'cancelled'].includes(p.status));
  const drafts = plans.filter(p => p.status === 'draft');

  return (
    <div className="space-y-6">
      {/* Active plans */}
      {active.length > 0 && (
        <PlanGroup title={t.groupActive} plans={active} onSelect={onSelect} isAthlete={isAthlete} highlight />
      )}

      {/* Drafts: SÓ aparecem para quem efetivamente edita treino físico.
          Antes era `!isAthlete` (todos os staff viam), o que dava ao Gestor
          e ao Treinador visibilidade de rascunhos que não são deles para
          editar. Agora condicionado a `canEditFisico` (= apenas Preparador). */}
      {canEditFisico && drafts.length > 0 && (
        <PlanGroup title={t.groupDrafts} plans={drafts} onSelect={onSelect} isAthlete={isAthlete} />
      )}

      {/* Past */}
      {past.length > 0 && (
        <PlanGroup title={t.groupPast} plans={past} onSelect={onSelect} isAthlete={isAthlete} collapsed />
      )}
    </div>
  );
};

const PlanGroup = ({ title, plans, onSelect, isAthlete, highlight, collapsed: defaultCollapsed }) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);

  return (
    <div>
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 mb-3 text-xs font-bold uppercase tracking-wider"
        style={{ color: VUKZ.textLight }}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        {title} ({plans.length})
      </button>

      {!collapsed && (
        <div className="space-y-3">
          {plans.map((plan) => (
            <button
              key={plan.plan_id}
              onClick={() => onSelect(plan.plan_id)}
              className="w-full text-left rounded-xl border p-4 hover:shadow-md transition-all group"
              style={{
                borderColor: highlight ? VUKZ.primary + '30' : VUKZ.border,
                backgroundColor: highlight ? VUKZ.primary + '05' : 'white',
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-sm truncate group-hover:underline" style={{ color: VUKZ.primary }}>
                      {plan.title}
                    </h3>
                    <StatusBadge status={plan.status} />
                  </div>
                  <p className="text-xs line-clamp-1" style={{ color: VUKZ.textLight }}>
                    {plan.sport_type || t.sportGeneral}
                    {!isAthlete && plan.athlete_name && ` · ${plan.athlete_name}`}
                    {plan.scheduled_date && ` · ${new Date(plan.scheduled_date).toLocaleDateString(t.dateLocale)}`}
                  </p>
                  {plan.description && (
                    <p className="text-xs mt-1 line-clamp-1" style={{ color: VUKZ.textLight }}>
                      {plan.description}
                    </p>
                  )}
                </div>
                <ChevronRight size={18} className="flex-shrink-0 mt-1" style={{ color: VUKZ.textLight }} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Plan Detail View
// ══════════════════════════════════════════════════════════════
const PlanDetailView = ({
  plan, loading, isAthlete, executionData, loadingExecution,
  onBack, onStartExecution, onToggleItem, onOpenFeedback,
}) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  if (loading || !plan) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  const blocks = plan.blocks || [];
  const canExecute = isAthlete && ['published', 'planned', 'in_progress'].includes(plan.status);
  const isExecuting = !!executionData;

  // Compute execution progress
  const totalItems = isExecuting
    ? (executionData.blocks || []).reduce((sum, b) => sum + (b.items || []).length, 0)
    : 0;
  const doneItems = isExecuting
    ? (executionData.blocks || []).reduce((sum, b) => sum + (b.items || []).filter(it => it.is_done).length, 0)
    : 0;
  const progressPct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Back button + header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-sm font-medium px-3 py-1.5 rounded-lg border hover:bg-neutral-50 transition-colors"
          style={{ borderColor: VUKZ.border, color: VUKZ.text }}
        >
          {t.back}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold" style={{ color: VUKZ.primary }}>{plan.title}</h2>
            <StatusBadge status={plan.status} />
          </div>
          <p className="text-xs" style={{ color: VUKZ.textLight }}>
            {plan.sport_type || t.sportGeneral}
            {plan.athlete_name && ` · ${plan.athlete_name}`}
            {plan.scheduled_date && ` · ${new Date(plan.scheduled_date).toLocaleDateString(t.dateLocale)}`}
          </p>
        </div>
      </div>

      {plan.description && (
        <p className="text-sm rounded-lg p-3" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}>
          {plan.description}
        </p>
      )}

      {/* Execution bar (athlete only) */}
      {canExecute && (
        <div className="rounded-xl border p-4" style={{ borderColor: VUKZ.primary + '40' }}>
          {isExecuting ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold" style={{ color: VUKZ.primary }}>
                  {t.executionInProgress}
                </span>
                <span className="text-xs font-bold" style={{ color: VUKZ.primary }}>
                  {doneItems}/{totalItems} ({progressPct}%)
                </span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
                <div
                  className="h-2 rounded-full transition-all"
                  style={{ width: `${progressPct}%`, backgroundColor: VUKZ.primary }}
                />
              </div>
              {progressPct === 100 && (
                <button
                  onClick={onOpenFeedback}
                  className="w-full py-2.5 rounded-lg text-white text-sm font-bold transition-colors hover:opacity-90"
                  style={{ backgroundColor: VUKZ.success }}
                >
                  {t.concludeWorkout}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => onStartExecution(plan.plan_id)}
              disabled={loadingExecution}
              className="w-full py-2.5 rounded-lg text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: VUKZ.primary }}
            >
              <Play size={16} />
              {loadingExecution ? t.loadingExec : t.startWorkout}
            </button>
          )}
        </div>
      )}

      {/* Blocks + Items */}
      {(isExecuting ? executionData.blocks : blocks).map((block, bi) => (
        <div key={bi} className="rounded-xl border" style={{ borderColor: VUKZ.border }}>
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: VUKZ.border }}>
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: VUKZ.primary }}
            >
              {bi + 1}
            </div>
            <h4 className="text-sm font-bold" style={{ color: VUKZ.primary }}>
              {block.title || block.block_title || t.blockLabel(bi + 1)}
            </h4>
          </div>

          <div className="divide-y" style={{ borderColor: VUKZ.border + '60' }}>
            {(block.items || []).length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: VUKZ.textLight }}>{t.noExercise}</p>
            ) : (
              (block.items || []).map((item, ii) => (
                <div
                  key={ii}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 transition-colors"
                >
                  {/* Checkbox (athlete execution mode) */}
                  {isExecuting && (
                    <button
                      onClick={() => onToggleItem(item.item_id || item.execution_item_id, item.is_done)}
                      className="flex-shrink-0"
                    >
                      <div
                        className="w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all"
                        style={{
                          borderColor: item.is_done ? VUKZ.success : VUKZ.border,
                          backgroundColor: item.is_done ? VUKZ.success : 'transparent',
                        }}
                      >
                        {item.is_done && <CheckCircle2 size={14} color="white" />}
                      </div>
                    </button>
                  )}

                  {/* Exercise info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium"
                      style={{
                        color: item.is_done ? VUKZ.textLight : VUKZ.text,
                        textDecoration: item.is_done ? 'line-through' : 'none',
                      }}
                    >
                      {item.description || item.title || t.exerciseFallback}
                    </p>
                    {item.notes && (
                      <p className="text-xs mt-0.5" style={{ color: VUKZ.textLight }}>{item.notes}</p>
                    )}
                  </div>

                  {/* Metrics badges */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    {item.sets_count && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#eff6ff', color: '#1e40af' }}>
                        {item.sets_count}x
                      </span>
                    )}
                    {item.reps_count && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#eff6ff', color: '#1e40af' }}>
                        {item.reps_count} reps
                      </span>
                    )}
                    {item.duration_seconds && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                        {Math.round(item.duration_seconds / 60)}min
                      </span>
                    )}
                    {item.rest_seconds > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                        {t.restLabel(item.rest_seconds)}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}

      {/* Feedback (if plan is completed) */}
      {plan.feedback && (
        <div className="rounded-xl border p-4" style={{ borderColor: VUKZ.border, backgroundColor: '#f0fdf4' }}>
          <h4 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#166534' }}>
            {t.feedbackTitle}
          </h4>
          <div className="grid grid-cols-3 gap-3 mb-2">
            {plan.feedback.rpe_actual != null && (
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: VUKZ.text }}>{plan.feedback.rpe_actual}/10</p>
                <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.fbRpe}</p>
              </div>
            )}
            {plan.feedback.feeling && (
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: VUKZ.text }}>{plan.feedback.feeling}</p>
                <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.fbFeeling}</p>
              </div>
            )}
            {plan.feedback.completion_pct != null && (
              <div className="text-center">
                <p className="text-lg font-bold" style={{ color: VUKZ.text }}>{plan.feedback.completion_pct}%</p>
                <p className="text-xs" style={{ color: VUKZ.textLight }}>{t.fbCompletion}</p>
              </div>
            )}
          </div>
          {plan.feedback.notes && (
            <p className="text-sm" style={{ color: VUKZ.text }}>{plan.feedback.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Executions Overview (Coach/Treinador view)
// ══════════════════════════════════════════════════════════════
const ExecutionsOverview = ({ executions }) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  if (executions.length === 0) {
    return (
      <div className="text-center py-16">
        <Users size={48} className="mx-auto mb-4" style={{ color: VUKZ.textLight }} />
        <h3 className="text-base font-bold mb-2" style={{ color: VUKZ.text }}>{t.noExecutions}</h3>
        <p className="text-sm" style={{ color: VUKZ.textLight }}>
          {t.noExecutionsHint}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b" style={{ borderColor: VUKZ.border }}>
            <th className="pb-3 text-left text-xs font-bold" style={{ color: VUKZ.textLight }}>{t.colAthlete}</th>
            <th className="pb-3 text-center text-xs font-bold" style={{ color: VUKZ.textLight }}>{t.colTotal}</th>
            <th className="pb-3 text-center text-xs font-bold" style={{ color: VUKZ.textLight }}>{t.colCompleted}</th>
            <th className="pb-3 text-center text-xs font-bold" style={{ color: VUKZ.textLight }}>{t.colAvgRpe}</th>
            <th className="pb-3 text-center text-xs font-bold" style={{ color: VUKZ.textLight }}>{t.colCompletion}</th>
          </tr>
        </thead>
        <tbody className="divide-y" style={{ borderColor: VUKZ.border + '60' }}>
          {executions.map((ex, i) => {
            const rpeColor =
              ex.avg_rpe == null ? VUKZ.textLight
              : ex.avg_rpe <= 6 ? VUKZ.success
              : ex.avg_rpe <= 8 ? VUKZ.warning
              : VUKZ.error;
            return (
              <tr key={i} className="hover:bg-neutral-50 transition-colors">
                <td className="py-3">
                  <span className="text-sm font-bold" style={{ color: VUKZ.primary }}>
                    {ex.athlete_name || `Atleta ${ex.athlete_user_id}`}
                  </span>
                </td>
                <td className="py-3 text-center text-sm">{ex.total_plans || 0}</td>
                <td className="py-3 text-center text-sm font-bold" style={{ color: VUKZ.success }}>
                  {ex.completed_plans || 0}
                </td>
                <td className="py-3 text-center">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                    style={{ backgroundColor: rpeColor + '20', color: rpeColor }}
                  >
                    {ex.avg_rpe != null ? ex.avg_rpe : '—'}
                  </span>
                </td>
                <td className="py-3 text-center text-sm font-bold">
                  {ex.avg_completion_pct != null ? `${ex.avg_completion_pct}%` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Feedback Modal (Athlete completes training)
// ══════════════════════════════════════════════════════════════
const FeedbackModal = ({ onSubmit, onClose }) => {
  const { lang } = useSportsLang();
  const t = getT(lang);
  const [rpe, setRpe] = useState(5);
  const [feeling, setFeeling] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const feelings = [
    { value: 'great', label: t.feelingGreat, color: VUKZ.success },
    { value: 'good', label: t.feelingGood, color: '#3b82f6' },
    { value: 'neutral', label: t.feelingNormal, color: VUKZ.warning },
    { value: 'tired', label: t.feelingTired, color: '#f97316' },
    { value: 'pain', label: t.feelingPain, color: VUKZ.error },
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    await onSubmit({ rpe_actual: rpe, feeling, notes, completion_pct: 100 });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold" style={{ color: VUKZ.primary }}>{t.modalTitle}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* RPE Slider */}
        <div className="mb-5">
          <label className="text-xs font-bold block mb-2" style={{ color: VUKZ.text }}>
            {t.rpeLabel} <span className="text-lg">{rpe}</span>/10
          </label>
          <input
            type="range"
            min="0"
            max="10"
            value={rpe}
            onChange={(e) => setRpe(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer"
            style={{ accentColor: VUKZ.primary }}
          />
          <div className="flex justify-between text-xs mt-1" style={{ color: VUKZ.textLight }}>
            <span>{t.rpeNone}</span>
            <span>{t.rpeMax}</span>
          </div>
        </div>

        {/* Feeling */}
        <div className="mb-5">
          <label className="text-xs font-bold block mb-2" style={{ color: VUKZ.text }}>{t.feelingLabel}</label>
          <div className="flex gap-2 flex-wrap">
            {feelings.map((f) => (
              <button
                key={f.value}
                onClick={() => setFeeling(f.value)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all"
                style={{
                  borderColor: feeling === f.value ? f.color : VUKZ.border,
                  backgroundColor: feeling === f.value ? f.color + '15' : 'white',
                  color: feeling === f.value ? f.color : VUKZ.textLight,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="text-xs font-bold block mb-2" style={{ color: VUKZ.text }}>{t.notesLabel}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2"
            style={{ borderColor: VUKZ.border }}
            placeholder={t.notesPlaceholder}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3 rounded-lg text-white text-sm font-bold transition-colors hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: VUKZ.primary }}
        >
          {submitting ? t.submitting : t.confirm}
        </button>
      </div>
    </div>
  );
};

export default TreinoFisicoPage;
